import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { format, parseISO, startOfDay } from "date-fns";
import { it, enUS } from "date-fns/locale";
import { http } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Users, Bed, Bath, Check, ArrowLeft, ChefHat, Wifi, Wind, Tv, WashingMachine, Bath as BathIcon, UtensilsCrossed, Calendar as CalendarIcon } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { resolveImg, onImgError } from "@/lib/images";
import useSEO from "@/lib/useSEO";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const MS_PER_DAY = 86_400_000;

export default function PropertyDetail() {
  const { t, lang } = useLang();
  const locale = lang === "en" ? enUS : it;
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({
    guest_name: "", guest_email: "", guest_phone: "",
    check_in: "", check_out: "", guests: 1, message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    http.get(`/properties/${id}`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [id]);

  const seoDesc = p ? ((lang === "en" && p.short_description_en) ? p.short_description_en : p.short_description) : "";
  useSEO({
    lang, path: `/proprieta/${id}`,
    title: p ? `${p.name} — Il Melograno apARTments` : "Il Melograno apARTments",
    description: seoDesc,
    image: p?.images?.[0] ? (p.images[0].startsWith("/api/") ? `https://www.ilmelograno.info${p.images[0]}` : p.images[0]) : undefined,
  });

  if (!p) {
    return <PublicLayout><div className="mel-container py-24" data-testid="prop-not-found">{t.common.loading}</div></PublicLayout>;
  }

  const nights = form.check_in && form.check_out
    ? Math.max(0, Math.round((new Date(form.check_out) - new Date(form.check_in)) / MS_PER_DAY))
    : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.check_in || !form.check_out) {
      toast.error(t.detail.selectDate);
      return;
    }
    setSubmitting(true);
    try {
      await http.post("/bookings/request", { ...form, guests: Number(form.guests), property_id: p.property_id });
      toast.success(t.detail.success);
      setForm({ guest_name: "", guest_email: "", guest_phone: "", check_in: "", check_out: "", guests: 1, message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t.detail.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  const nightLabel = nights === 1 ? t.common.night : t.common.nights;
  const bedLabel = p.beds === 1 ? t.common.bed : t.common.beds;
  const bathLabel = p.baths === 1 ? t.common.bath : t.common.baths;

  return (
    <PublicLayout>
      <section className="mel-container pt-12 pb-8">
        <Link to="/proprieta" className="inline-flex items-center gap-2 text-sm text-[var(--mel-muted)] hover:text-[var(--mel-primary)]" data-testid="prop-back">
          <ArrowLeft size={16} /> {t.common.backAll}
        </Link>
        <div className="mt-6 flex flex-wrap items-baseline justify-between gap-4">
          <div>
            <div className="eyebrow mb-2">{p.type === "loft" ? t.properties.loft : t.properties.room}</div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="prop-name">{p.name}</h1>
          </div>
        </div>
      </section>

      <section className="mel-container pb-8 grid md:grid-cols-4 gap-4">
        <div className="md:col-span-3 h-[400px] md:h-[500px] w-full rounded-md overflow-hidden">
          <img
            src={resolveImg(p.images?.[activeImg])}
            onError={onImgError}
            alt={p.name}
            className="w-full h-full object-cover"
            data-testid="prop-main-image"
          />
        </div>
        <div className="md:col-span-1 flex md:flex-col gap-4 overflow-x-auto">
          {(p.images || []).map((src, i) => (
            <button
              key={src}
              onClick={() => setActiveImg(i)}
              className={`h-24 md:h-32 w-40 md:w-full rounded-md overflow-hidden border-2 transition-colors flex-shrink-0 ${activeImg === i ? "border-[var(--mel-primary)]" : "border-transparent"}`}
              data-testid={`prop-thumb-${i}`}
            >
              <img src={resolveImg(src)} onError={onImgError} alt={`${p.name} — foto ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      </section>

      <section className="mel-container pb-24 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-8 pb-6 border-b border-[var(--mel-border)]">
            <div className="flex items-center gap-2"><Users size={18} /><span>{p.max_guests} {t.common.guests}</span></div>
            <div className="flex items-center gap-2"><Bed size={18} /><span>{p.beds} {bedLabel}</span></div>
            <div className="flex items-center gap-2"><Bath size={18} /><span>{p.baths} {bathLabel}</span></div>
          </div>
          <h2 className="font-serif text-2xl mt-10 mb-4">{t.detail.house}</h2>
          <p className="text-[var(--mel-muted)] leading-relaxed whitespace-pre-line">{(lang === "en" && p.description_en) ? p.description_en : p.description}</p>

          <h2 className="font-serif text-2xl mt-12 mb-6">{t.detail.services}</h2>
          <div className="grid grid-cols-2 gap-4">
            {(p.amenities || []).map((a) => {
              const key = a.toLowerCase();
              let Icon = Check;
              if (key.includes("wi-fi") || key.includes("wifi")) Icon = Wifi;
              else if (key.includes("aria")) Icon = Wind;
              else if (key.includes("chromecast") || key.includes("tv") || key.includes("televis")) Icon = Tv;
              else if (key.includes("cucina in comune")) Icon = ChefHat;
              else if (key.includes("cucina")) Icon = UtensilsCrossed;
              else if (key.includes("bagno")) Icon = BathIcon;
              else if (key.includes("lavatrice")) Icon = WashingMachine;
              const AMENITY_EN = {
                "wi-fi": "Wi-Fi",
                "aria condizionata": "Air conditioning",
                "chromecast": "Chromecast",
                "cucina attrezzata": "Fully equipped kitchen",
                "cucina in comune": "Shared kitchen",
                "lavatrice": "Washing machine",
                "bagno privato": "Private bathroom",
              };
              const label = lang === "en" ? (AMENITY_EN[key] || a) : a;
              return (
                <div key={a} className="flex items-center gap-3 text-sm">
                  <Icon size={16} className="text-[var(--mel-primary)]" /> {label}
                </div>
              );
            })}
          </div>
        </div>

        <aside className="lg:col-span-1">
          <form onSubmit={submit} className="border border-[var(--mel-border)] rounded-md p-6 md:p-8 sticky top-24 bg-[var(--mel-bg)]" data-testid="booking-form">
            <div className="font-serif text-xl mb-6">{t.detail.requestBooking}</div>
            <label className="text-xs eyebrow">{t.detail.yourName}</label>
            <input required value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className="w-full mt-1 mb-4 border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm bg-white" data-testid="booking-name" />
            <label className="text-xs eyebrow">{t.detail.yourEmail}</label>
            <input required type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} className="w-full mt-1 mb-4 border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm bg-white" data-testid="booking-email" />
            <label className="text-xs eyebrow">{t.detail.phone}</label>
            <input value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} className="w-full mt-1 mb-4 border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm bg-white" data-testid="booking-phone" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs eyebrow">{t.detail.checkin}</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="booking-checkin"
                      className={`w-full mt-1 border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm bg-white flex items-center justify-between ${form.check_in ? '' : 'text-[var(--mel-muted)]'}`}
                    >
                      {form.check_in ? format(parseISO(form.check_in), "dd/MM/yyyy", { locale }) : t.detail.selectDate}
                      <CalendarIcon size={14} className="text-[var(--mel-muted)]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      locale={locale}
                      weekStartsOn={1}
                      selected={form.check_in ? parseISO(form.check_in) : undefined}
                      onSelect={(d) => setForm({ ...form, check_in: d ? format(d, "yyyy-MM-dd") : "", check_out: d && form.check_out && parseISO(form.check_out) <= d ? "" : form.check_out })}
                      disabled={{ before: startOfDay(new Date()) }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-xs eyebrow">{t.detail.checkout}</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="booking-checkout"
                      className={`w-full mt-1 border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm bg-white flex items-center justify-between ${form.check_out ? '' : 'text-[var(--mel-muted)]'}`}
                    >
                      {form.check_out ? format(parseISO(form.check_out), "dd/MM/yyyy", { locale }) : t.detail.selectDate}
                      <CalendarIcon size={14} className="text-[var(--mel-muted)]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      locale={locale}
                      weekStartsOn={1}
                      selected={form.check_out ? parseISO(form.check_out) : undefined}
                      onSelect={(d) => setForm({ ...form, check_out: d ? format(d, "yyyy-MM-dd") : "" })}
                      disabled={{ before: form.check_in ? startOfDay(new Date(new Date(form.check_in).getTime() + MS_PER_DAY)) : startOfDay(new Date()) }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <label className="text-xs eyebrow mt-4 block">{t.detail.guests}</label>
            <input required type="number" min="1" max={p.max_guests} value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className="w-full mt-1 mb-4 border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm bg-white" data-testid="booking-guests" />
            <label className="text-xs eyebrow">{t.detail.message}</label>
            <textarea rows="3" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full mt-1 mb-4 border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm bg-white" data-testid="booking-message" />

            {nights > 0 && (
              <div className="mb-4 p-3 rounded-md bg-[var(--mel-surface)] text-sm text-center" data-testid="booking-total">
                {nights} {nightLabel}
              </div>
            )}

            <button type="submit" disabled={submitting} className="mel-btn-primary w-full justify-center" data-testid="booking-submit">
              {submitting ? t.common.sending : t.detail.sendRequest}
            </button>
            <p className="text-xs text-[var(--mel-muted)] mt-3">{t.detail.disclaimer}</p>
          </form>
        </aside>
      </section>
    </PublicLayout>
  );
}
